import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../../components/ui/Header';
import ConversationList from './components/ConversationList';
import ChatPanel from './components/ChatPanel';
import EmptyState from './components/EmptyState';
import ConversationSettings from './components/ConversationSettings';
import { useAuth } from '../../contexts/AuthContext';
import { conversationsService, messagesService, profileService } from '../../services/supabaseService';
import { useLocation } from 'react-router-dom';

const MessagingSystem = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [currentUserId, setCurrentUserId] = useState(null);
  const [userGender, setUserGender] = useState('male');
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 1024);
  const [isLoading, setIsLoading] = useState(true);

  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState({});
  const [messageChannel, setMessageChannel] = useState(null);

  useEffect(() => {
    if (user) {
      setCurrentUserId(user?.id);
      loadUserData();
      loadConversations();
    }
  }, [user]);

  useEffect(() => {
    // Handle starting new conversation from navigation state
    if (location?.state?.startConversationWith && user) {
      startNewConversation(location?.state?.startConversationWith);
    }
  }, [location?.state, user]);

  useEffect(() => {
    // Subscribe to real-time messages for active conversation
    if (activeConversationId) {
      loadMessages(activeConversationId);
      subscribeToConversation(activeConversationId);
    }

    return () => {
      if (messageChannel) {
        messagesService?.unsubscribeFromMessages(messageChannel);
      }
    };
  }, [activeConversationId]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadUserData = async () => {
    try {
      const profile = await profileService?.getProfile(user?.id);
      setUserGender(profile?.gender || 'male');
      // Check for active subscription (premium, active, or trialing)
      const isActive = 
        profile?.subscriptionStatus === 'active' || 
        profile?.subscriptionStatus === 'premium' ||
        profile?.subscriptionStatus === 'trialing';
      setHasActiveSubscription(isActive);
    } catch (error) {
      console.error('Load user data error:', error);
    }
  };

  const loadConversations = async () => {
    setIsLoading(true);
    try {
      const data = await conversationsService?.getMyConversations(user?.id);
      
      // Transform conversations to match component format
      const transformedConversations = data?.map(conv => {
        const otherUser = conv?.participantOneId === user?.id ? conv?.participantTwo : conv?.participantOne;
        return {
          id: conv?.id,
          userId: otherUser?.id,
          userName: otherUser?.displayName || 'Anonymous User',
          avatar: otherUser?.primaryPhoto || '',
          avatarAlt: otherUser?.primaryPhotoAlt || 'User avatar',
          isOnline: otherUser?.isOnline || false,
          isVerified: otherUser?.isVerified || false,
          photoBlurred: otherUser?.photoBlurred || false,
          lastMessage: '',
          lastMessageTime: new Date(conv?.lastMessageAt),
          lastMessageType: 'text',
          unreadCount: 0,
          lastSeen: new Date(otherUser?.lastActive)
        };
      }) || [];

      setConversations(transformedConversations);

      // Load last message for each conversation
      for (const conv of transformedConversations) {
        loadLastMessage(conv?.id);
      }
    } catch (error) {
      console.error('Load conversations error:', error);
      setConversations([]);
    } finally {
      setIsLoading(false);
    }
  };

  const loadLastMessage = async (conversationId) => {
    try {
      const msgs = await messagesService?.getConversationMessages(conversationId);
      if (msgs?.length > 0) {
        const lastMsg = msgs?.[msgs?.length - 1];
        setConversations(prev => prev?.map(conv => 
          conv?.id === conversationId
            ? {
                ...conv,
                lastMessage: lastMsg?.messageText || 'Media message',
                lastMessageType: lastMsg?.messageType,
                unreadCount: msgs?.filter(m => m?.receiverId === user?.id && m?.status !== 'read')?.length || 0
              }
            : conv
        ));
      }
    } catch (error) {
      console.error('Load last message error:', error);
    }
  };

  const loadMessages = async (conversationId) => {
    try {
      const data = await messagesService?.getConversationMessages(conversationId);
      
      // Transform messages to match component format
      const transformedMessages = data?.map(msg => ({
        id: msg?.id,
        senderId: msg?.senderId,
        text: msg?.messageText,
        type: msg?.messageType,
        timestamp: new Date(msg?.createdAt),
        status: msg?.status,
        imageUrl: msg?.imageUrl,
        imageAlt: msg?.imageAlt,
        blurred: msg?.isBlurred,
        requiresApproval: msg?.requiresApproval,
        approved: msg?.isApproved,
        expiresIn: msg?.expiresIn
      })) || [];

      setMessages(prev => ({
        ...prev,
        [conversationId]: transformedMessages
      }));

      // Mark messages as read
      const unreadMessages = data?.filter(m => m?.receiverId === user?.id && m?.status !== 'read');
      for (const msg of unreadMessages) {
        await messagesService?.markMessageAsRead(msg?.id);
      }
    } catch (error) {
      console.error('Load messages error:', error);
    }
  };

  const subscribeToConversation = (conversationId) => {
    const channel = messagesService?.subscribeToMessages(conversationId, (newMessage) => {
      setMessages(prev => ({
        ...prev,
        [conversationId]: [...(prev?.[conversationId] || []), {
          id: newMessage?.id,
          senderId: newMessage?.senderId,
          text: newMessage?.messageText,
          type: newMessage?.messageType,
          timestamp: new Date(newMessage?.createdAt),
          status: newMessage?.status
        }]
      }));

      // Update conversation last message
      loadLastMessage(conversationId);
    });

    setMessageChannel(channel);
  };

  const startNewConversation = async (otherUserId) => {
    try {
      const conversation = await conversationsService?.getOrCreateConversation(user?.id, otherUserId);
      setActiveConversationId(conversation?.id);
      
      // Reload conversations to include new one
      loadConversations();
    } catch (error) {
      console.error('Start conversation error:', error);
    }
  };

  const handleSelectConversation = (conversationId) => {
    setActiveConversationId(conversationId);
    if (isMobileView) {
      // Mobile: show chat panel
    }
  };

  const handleSendMessage = async (messageData) => {
    if (!activeConversationId || !user) return;

    const conversation = conversations?.find(c => c?.id === activeConversationId);
    if (!conversation) return;

    try {
      await messagesService?.sendMessage(
        activeConversationId,
        user?.id,
        conversation?.userId,
        messageData?.text,
        messageData?.type
      );
    } catch (error) {
      console.error('Send message error:', error);
      alert('Failed to send message. Please try again.');
    }
  };

  const handleBack = () => {
    setActiveConversationId(null);
  };

  const activeConversation = conversations?.find(c => c?.id === activeConversationId);
  const conversationMessages = messages?.[activeConversationId] || [];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading conversations...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Messages - Bamchik Discreet Dating</title>
        <meta name="description" content="Secure and private messaging on Bamchik discreet dating platform" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />

        <main className="mx-auto max-w-[1400px] px-4 lg:px-6 py-6 md:py-8 lg:py-10">
          <div className="mb-6 md:mb-8">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold mb-2">
              Messages
            </h1>
            <p className="text-sm md:text-base text-muted-foreground">
              Secure and private conversations
            </p>
          </div>

          <div className="bg-card rounded-xl shadow-warm overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-3 h-[calc(100vh-16rem)] lg:h-[600px]">
              <div className={`lg:col-span-1 border-r border-border ${
                isMobileView && activeConversationId ? 'hidden' : 'block'
              }`}>
                <ConversationList
                  conversations={conversations}
                  activeConversationId={activeConversationId}
                  onSelectConversation={handleSelectConversation}
                  searchQuery={searchQuery}
                  onSearchChange={setSearchQuery}
                />
              </div>

              <div className={`lg:col-span-2 ${
                isMobileView && !activeConversationId ? 'hidden' : 'block'
              }`}>
                {activeConversation ? (
                  <ChatPanel
                    conversation={activeConversation}
                    messages={conversationMessages}
                    onSendMessage={handleSendMessage}
                    onBack={handleBack}
                    currentUserId={currentUserId}
                    userGender={userGender}
                    hasActiveSubscription={hasActiveSubscription}
                  />
                ) : (
                  <EmptyState onStartConversation={(userId) => startNewConversation(userId)} />
                )}
              </div>
            </div>
          </div>
        </main>

        {showSettings && activeConversation && (
          <ConversationSettings
            conversation={activeConversation}
            onClose={() => setShowSettings(false)}
            onUpdateSettings={(settings) => {
              // Handle settings update
              console.log('Settings updated:', settings);
            }}
          />
        )}
      </div>
    </>
  );
};

export default MessagingSystem;